import crypto from 'crypto';
import produce from 'immer';
import i18n from './ChrI18n';

const cloneObj = (obj) => {
  return { ...obj };
};

const cloneArray = (array) => {
  return array.map((element) => cloneObj(element));
};

const safeMutate = produce;

const decodeString = (str) => {
  return new DOMParser().parseFromString(str, "text/html").documentElement.textContent;
};

const digestString = (str) => {
  return crypto.createHash('md5').update(str).digest('hex');
};

const mergeObjectsArray = (objectsArray) => {
  return objectsArray.reduce((acc, curr) => ({ ...acc, ...curr }), {});
};

const getObjAttributesSize = (object) => {
  return Object.keys(object).length;
};

const filterObjectByType = (object, type) => {
  return Object.entries(object).reduce((acc, [key, val]) => {
    if (typeof (val) == type) {
      acc[key] = val;
    }
    return acc;
  }, {});
};

const filterObjectByKeys = (obj, filterKeys) => {
  return filterKeys.reduce((result, key) => {
    if (obj.hasOwnProperty(key)) {
      result[key] = obj[key];
    }
    return result;
  }, {});
};

const filterObjectByValues = (obj, filterVals) => {
  return Object.keys(obj).reduce((acc, curr) => {
    if (filterVals.indexOf(obj[curr]) !== -1) {
      acc[curr] = obj[curr];
    }
    return acc;
  }, {});
};

const filterObjectByKeyPath = (obj, filterVals, keyPath = "") => {
  return Object.keys(obj).reduce((acc, curr) => {
    if (filterVals.indexOf(getValueByKeyPath(obj[curr], keyPath)) !== -1) {
      acc[curr] = obj[curr];
    }
    return acc;
  }, {});
};

const getValueByKeyPath = (object, keysPath, defaultVal) => {
  let keys = keysPath.replace(/\[/g, '.').replace(/]/g, '').split('.');
  for (let i = 0; i < keys.length; i++) {
    if (object === undefined) {
      return defaultVal;
    } else {
      object = object[keys[i]];
    };
  }
  return object == undefined ? defaultVal : object;
};

const showHideElement = {
  show: (element) => {
    element.className = '';
    element.style.display = 'block';
  },

  hide: (element) => {
    element.className = 'hide';
    element.style.display = 'none';
  }
};

const ChrLoader = {
  show: () => showHideElement.show(document.getElementById('loading_results')),
  hide: () => showHideElement.hide(document.getElementById('loading_results'))
};

const getCsrfToken = () => {
  let csrfTokenElement = document.querySelectorAll('meta[name="csrf-token"]');
  if (!csrfTokenElement[0])
    return '';
  return csrfTokenElement[0].content;
};

const sleep = (time) => {
  return new Promise((resolve) => setTimeout(resolve, time));
};

const min = (val1, val2) => {
  return val1 <= val2 ? val1 : val2;
};

const max = (val1, val2) => {
  return val1 >= val2 ? val1 : val2;
};

const noop = () => { };

const getGroupBy = (arr, key) => {
  return arr.reduce((obj, value) => { obj[value[key]] = obj[value[key]] || []; obj[value[key]].push(value); return obj; }, {});
};

const isEmptyObject = (object) => {
  return (Object.keys(object).length === 0 && object.constructor === Object);
};

const showElementBySelector = (selector) => {
  document.querySelector(selector).classList.remove("hide");
};

const hideElementBySelector = (selector) => {
  document.querySelector(selector).classList.add('hide');
};

const changeLoaderText = (text) => {
  document.querySelector('#loading_results .cjs_loading_text').innerText = text;
};

const showOrHidePageLoader = (show) => {
  let pageLoader = document.querySelector("#loading_results");
  if (show) {
    pageLoader.style.display = "block";
    showElementBySelector("#loading_results");
  }
  else {
    pageLoader.style.display = "none";
    hideElementBySelector("#loading_results");
  }
};

const isElementVisibleOnScreen = (selector) => {
  return !(document.querySelector(selector).classList.contains("hide"));
};

const addEventListener = (selector, listenerFunction, eventType) => {
  let elements = document.querySelectorAll(selector);
  elements.forEach(element => element.removeEventListener(eventType, listenerFunction));
  elements.forEach(element => element.addEventListener(eventType, listenerFunction));
};

const addClickEventListener = (selector, listenerFunction) => {
  addEventListener(selector, listenerFunction, "click");
  addEventListener(selector, (event) => {
    if(event.key === 'Enter') listenerFunction(event);
  }, "keydown");
};

const removeEventListener = (selector, listenerFunction, eventType) => {
  let elements = document.querySelectorAll(selector);
  elements.forEach(element => element.removeEventListener(eventType, listenerFunction));
};

const scrollToTop = () => {
  document.body.scrollTop = 0; // For Safari
  document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE
};

const reloadPage = (props = {}) => {
  if (props.showLoader)
    ChrLoader.show();
  window.location.reload();
};

const redirectTo = (redirectUrl) => {
  window.location.href = redirectUrl;
}

const checkValidHexColorCode = (colorCode) => {
  let regex = /^#([0-9a-f]{3}){1,2}$/;
  return regex.test(colorCode);
};

const collectIdsAsString = (entities) => {
  let ids = []
  entities.forEach((entity) => {
    ids.push(entity.dataset.id);
  })
  return ids.join(',');
}

const orHashValues = (hsh) => {
  return Object.values(hsh).reduce((acc, val) => acc || val, false)
}

const keyBoardAccessibilityAttributes = {
  tabIndex: 0,
  onKeyDown: event => handleKeyDown(event)
}

const handleKeyDown = event => {
  if(event.key === 'Enter') event.target.click();
}

const getCurrencyUnit = () => {
  return i18n.t('number.currency.format.unit');
};

const formatCurrency = (() => {
  const intlFormatterCache = {};

  return (amount, { withUnit = true, locale = 'en-US', formatterOptions = {} } = {}) => {
    const cacheKey = `${locale}_${JSON.stringify(formatterOptions)}`;
    if (intlFormatterCache[cacheKey] === undefined) {
      intlFormatterCache[cacheKey] = new Intl.NumberFormat(locale, formatterOptions);
    }
    const unit = withUnit ? getCurrencyUnit() : '';
    const sign = amount < 0 ? '-' : '';

    return `${sign}${unit}${intlFormatterCache[cacheKey].format(Math.abs(Number(amount)))}`;
  };
})();

const splitIntegerEvenly = (integerToSplit, numberOfSplits) => {
  let splitValue = Math.floor(integerToSplit / numberOfSplits);
  let splitReminderValue = integerToSplit % numberOfSplits;

  return Array.from({length: numberOfSplits}, () => {
    let result = splitValue;

    if(splitReminderValue > 0) {
      result++;
      splitReminderValue--;
    }
    return result;
  });
};

const assignNewKeysOnly = (target, source) => {
  Object.keys(source).forEach(key => {
    if (!(key in target)) {
      target[key] = source[key];
    }
  });
};

const isEmptyString = (str) => !str || str.trim().length === 0;

const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '';

  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    // Single word - take first character only
    return words[0].charAt(0).toUpperCase();
  } else {
    // Multiple words - take first letter of first and last word
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }
};

const cleanWhitespaceAndLineBreaks = (content) => {
  return content.replace(/<br\s*\/?>/gi, '').replace(/&nbsp;/gi, '').replace(/\u200B/g, '').trim();
};

const isDevelopmentOrTestEnv = () => {
  const envs = ["development", "test"];
  return envs.includes(jsConstants.currentEnvironment);
}

export {
  cloneObj,
  cloneArray,
  safeMutate,
  decodeString,
  digestString,
  mergeObjectsArray,
  getObjAttributesSize,
  showHideElement,
  getCsrfToken,
  sleep,
  getValueByKeyPath,
  min,
  max,
  noop,
  filterObjectByKeys,
  filterObjectByType,
  filterObjectByValues,
  filterObjectByKeyPath,
  getGroupBy,
  showElementBySelector,
  hideElementBySelector,
  showOrHidePageLoader,
  isElementVisibleOnScreen,
  isEmptyObject,
  addEventListener,
  addClickEventListener,
  removeEventListener,
  scrollToTop,
  reloadPage,
  checkValidHexColorCode,
  changeLoaderText,
  ChrLoader,
  collectIdsAsString,
  orHashValues,
  keyBoardAccessibilityAttributes,
  getCurrencyUnit,
  formatCurrency,
  splitIntegerEvenly,
  assignNewKeysOnly,
  cleanWhitespaceAndLineBreaks,
  redirectTo,
  isDevelopmentOrTestEnv,
  isEmptyString,
  getInitials
};
